import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  IconBuildingBank,
  IconDashboard,
  IconPlus,
  IconSearch,
  IconSettings,
} from "@tabler/icons-react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/images/logo/logowhite.png";
import { useAuth } from "../../contexts/AuthContext";
import ProfileDropdown from "../navigation/ProfileDropdown";
import Button from "../ui/Button";

const DesktopLayout = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  // Get user data from Firebase auth with profile integration
  const isLoggedIn = !!currentUser;

  // Navigation menu items
  const menuItems = [
    {
      text: "Dashboard",
      icon: <IconDashboard size={20} />,
      path: "/dashboard",
    },
    {
      text: "Search Games",
      icon: <IconSearch size={20} />,
      path: "/upcoming",
    },
    {
      text: "Create Game",
      icon: <IconPlus size={20} />,
      path: "/create-game",
    },
    {
      text: "Payments",
      icon: <IconBuildingBank size={20} />,
      path: "/payments",
    },
    {
      text: "Settings",
      icon: <IconSettings size={20} />,
      path: "/settings",
    },
  ];

  const handleNavigation = (path) => {
    navigate(path);
  };

  // Only render desktop layout on desktop screens
  if (!isDesktop) {
    return <Outlet />;
  }

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {/* Left Sidebar */}
      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          width: { xs: 200, lg: 280 },
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: { xs: 200, lg: 280 },
            boxSizing: "border-box",
            backgroundColor: "#162456",
            borderRight: "1px solid #E8E8E8",
          },
        }}
      >
        {/* Logo Section */}
        <Box
          component={Link}
          to="/"
          sx={{
            p: 3,
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <img src={logo} alt="Logo" />
        </Box>

        {/* Navigation Menu */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <Box>
            <List sx={{ p: 0 }}>
              {menuItems.map((item) => (
                <ListItem
                  key={item.text}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    py: 1.5,
                    px: 0,
                    cursor: "pointer",
                    borderRadius: 0,
                    backgroundColor:
                      location.pathname === item.path
                        ? "#203FA9"
                        : "transparent",
                    "&:hover": {
                      backgroundColor:
                        location.pathname === item.path
                          ? "#203FA9"
                          : "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: "white",
                      minWidth: "0",
                      mr: "16px",
                      pl: 2,
                      "& svg": { fontSize: "20px" },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      "& .MuiTypography-root": {
                        fontSize: "16px",
                        fontWeight: location.pathname === item.path ? 600 : 400,
                        color: "white",
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Drawer>

      {/* Main Content Area with Header */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#f5f5f5",
        }}
      >
        {/* Top Header with Profile */}
        <Box
          sx={{
            backgroundColor: "white",
            borderBottom: "1px solid #E8E8E8",
            px: 3,
            py: 0,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            minHeight: "48px",
          }}
        >
          {isLoggedIn ? (
            <ProfileDropdown />
          ) : (
            <Button
              component={Link}
              to="/signin"
              sx={{
                height: 40,
                width: "auto",
                px: { md: 2, lg: "28px" },
              }}
            >
              Login or signup
            </Button>
          )}
        </Box>

        {/* Page Content */}
        <Box sx={{ flex: 1, overflow: "auto", padding: 3 }}>
          <Box sx={{ maxWidth: "864px", mx: "auto" }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DesktopLayout;
